/**
 * Pure helpers for the Discord-shape "thread_created" system-row
 * gating + source-id set extraction. Lifted out of
 * `use-channel-message-list.ts` so the rules can be unit-tested
 * directly without booting a Zero query subscription.
 *
 * Both helpers operate on the channel-rendered slice (top-level
 * messages, oldest→newest). They are used together: the filter
 * removes the "started a thread" announcement when the source has no
 * activity, and the source-id set drives the per-message
 * `threadActive` bool downstream.
 */

/**
 * Narrow shape — only the columns these helpers consult. Extend if you
 * add fields to the `thread_created` payload (the `actions.createThread`
 * mutator stamps `event` + `thread_message_id` + `started_by_user_id`;
 * we only read the first two here). Wider than `Record<string, unknown>`
 * would force unsafe casts in the body, narrower locks future additions
 * out of the type — the compromise is to enumerate exactly the fields
 * the helpers read.
 */
interface MinimalMessage {
  id: string;
  kind?: string | null;
  payload?: { event?: unknown; thread_message_id?: unknown } | null;
  reply_count?: number;
}

/**
 * True iff `m` is the Discord-shape "X started a thread" system row.
 * Identified by `kind === "system"` + `payload.event === "thread_created"`
 * (written by `actions.createThread` — see
 * `packages/shared/src/mutators/actions.ts:629-645`).
 */
export function isThreadCreatedSystemRow(m: MinimalMessage): boolean {
  if (m.kind !== "system") return false;
  const event = m.payload?.event;
  return event === "thread_created";
}

/**
 * Render filter: hide the "X started a thread" system row in the
 * channel timeline until its source has at least one reply. Without
 * this, clicking Create Thread injects a divider into the channel
 * even though nothing has been said in the thread yet.
 *
 * Rules (in order of evaluation):
 *   • Non-system rows: pass through.
 *   • thread_created rows whose `payload.thread_message_id` is missing
 *     or non-string: pass through. Defensive — the mutator always
 *     stamps a string, so this branch only fires on shape corruption.
 *     Asymmetric with the next bullet (which hides) — pass-through
 *     keeps the row visible as a degraded "[message]" pill that the
 *     user can still tap, surfacing the corruption rather than
 *     silently swallowing it.
 *   • thread_created rows whose source is NOT in the loaded slice:
 *     hide. The source may have paginated out; we don't know its
 *     `reply_count`, and a "X started a thread" announcement pointing
 *     into the void is worse than no announcement. The badge
 *     re-appears once the source loads back in.
 *   • thread_created rows whose source has `reply_count === 0`: hide.
 *   • thread_created rows whose source has `reply_count > 0`: pass.
 *
 * Trade-off: a paginated-out source that DOES have replies will have
 * its announcement hidden until the source loads in. Acceptable — the
 * announcement is a discovery aid, not load-bearing. The thread is
 * still reachable via the badge once the source returns to view.
 */
export function filterDeferredThreadCreatedRows<T extends MinimalMessage>(
  messages: readonly T[],
): T[] {
  const replyCountById = new Map<string, number>();
  for (const m of messages) {
    replyCountById.set(m.id, m.reply_count ?? 0);
  }
  return messages.filter((m) => {
    if (!isThreadCreatedSystemRow(m)) return true;
    const sourceId = m.payload?.thread_message_id;
    if (typeof sourceId !== "string") return true;
    return (replyCountById.get(sourceId) ?? 0) > 0;
  });
}

/**
 * Build the set of source-message IDs that have a thread_created
 * system row in the slice. Built from the UNFILTERED message list so
 * a hidden announcement (no replies yet) still keeps the source-id
 * absent from the set — the badge / side-bar gates on this set, and
 * we want the badge to appear only when the announcement is visible.
 *
 * Caller is expected to wrap the returned Set in `useStableSet` so
 * downstream consumers see a stable reference unless membership
 * actually changes.
 */
export function deriveThreadSourceIds(
  messages: readonly MinimalMessage[],
): Set<string> {
  const set = new Set<string>();
  for (const m of messages) {
    if (!isThreadCreatedSystemRow(m)) continue;
    const sourceId = m.payload?.thread_message_id;
    if (typeof sourceId === "string") set.add(sourceId);
  }
  return set;
}
